import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useAMap } from './hooks/useAMap';
import { useLocalStorage } from './hooks/useLocalStorage';
import { MapService } from './services/MapService';
import { STORAGE_KEYS } from './utils/constants';
import { isValidCoordinate } from './utils/validation';
import {
  GeocodingResult,
  GeocodingError,
  SearchProgress,
  GeocodingCandidate,
  AMapMarker
} from './types';

import Header from './components/Header';
import AddressInput from './components/AddressInput';
import MapContainer from './components/MapContainer';
import ResultsList from './components/ResultsList';
import CandidateModal from './components/CandidateModal';
import ApiKeyConfig from './components/ApiKeyConfig';

import './App.css';

const App: React.FC = () => {
  // 状态管理
  const [apiKey, setApiKey] = useLocalStorage(STORAGE_KEYS.API_KEY, '');
  const [securityKey, setSecurityKey] = useLocalStorage(STORAGE_KEYS.SECURITY_KEY, '');
  const [showConfig, setShowConfig] = useState<boolean>(false);
  const [showCandidateModal, setShowCandidateModal] = useState<boolean>(false);
  const [currentResult, setCurrentResult] = useState<GeocodingResult | null>(null);
  const [currentResultIndex, setCurrentResultIndex] = useState<number>(-1);
  const [results, setResults] = useState<GeocodingResult[]>([]);
  const [errors, setErrors] = useState<GeocodingError[]>([]);
  const [markers, setMarkers] = useState<(AMapMarker | undefined)[]>([]);
  const [isSearching, setIsSearching] = useState<boolean>(false);
  const [searchProgress, setSearchProgress] = useState<SearchProgress | null>(null);

  // Hooks
  const { AMap, map, geocoder, loading, error, initMap } = useAMap(apiKey, securityKey);

  // Refs
  const mapServiceRef = useRef<MapService | null>(null);

  // 初始化地图服务
  useEffect(() => {
    if (geocoder) {
      mapServiceRef.current = new MapService(geocoder);
    } else {
      mapServiceRef.current = null;
    }
  }, [geocoder]);

  // 检查是否需要显示配置界面
  useEffect(() => {
    if (!apiKey || !securityKey) {
      setShowConfig(true);
    }
  }, [apiKey, securityKey]);

  // 自动初始化地图（当有密钥但地图未初始化时）
  useEffect(() => {
    if (apiKey && securityKey && !map && !loading && !error) {
      const timer = setTimeout(() => {
        initMap();
      }, 500); // 给一点时间让DOM渲染

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [apiKey, securityKey, map, loading, error, initMap]);

  // 配置API密钥
  const handleConfigSave = useCallback(async (newApiKey: string, newSecurityKey: string) => {
    setApiKey(newApiKey);
    setSecurityKey(newSecurityKey);

    try {
      await initMap();
      setShowConfig(false);
    } catch (err) {
      console.error('地图初始化失败:', err);
    }
  }, [initMap, setApiKey, setSecurityKey]);

  // 处理结果点击（前置，供其他回调复用）
  const handleResultClick = useCallback((result: GeocodingResult, _index: number) => {
    if (map && isValidCoordinate(result.location)) {
      map.setCenter([result.location.lng, result.location.lat]);
      map.setZoom(16);
    }
  }, [map]);

  // 标记点击 → 行为与结果点击一致
  const handleMarkerClick = useCallback((result: GeocodingResult, index: number) => {
    handleResultClick(result, index);
  }, [handleResultClick]);

  // 清除地图标记（前置，供搜索时复用）
  const clearMarkers = useCallback(() => {
    if (mapServiceRef.current && map && markers) {
      mapServiceRef.current.clearMarkers(map, markers);
      setMarkers([]);
    }
  }, [map, markers]);

  // 搜索地址
  const handleSearch = useCallback(async (addresses: string[], prefix: string) => {
    if (!mapServiceRef.current) {
      alert('地图服务未初始化');
      return;
    }

    setIsSearching(true);
    setResults([]);
    setErrors([]);
    setMarkers([]);
    setSearchProgress({
      current: 0,
      total: addresses.length,
      success: 0,
      errors: 0
    });
    clearMarkers();

    try {
      const result = await mapServiceRef.current.batchGeocode(
        addresses,
        prefix,
        (progress) => {
          setSearchProgress({
            current: progress.current,
            total: progress.total,
            success: progress.success,
            errors: progress.errors
          });

          if (progress.results) {
            const sorted = [...progress.results].sort((a, b) => (
              (typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER) -
              (typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER)
            ));
            setResults(sorted);
          }

          if (progress.errorList) {
            setErrors(progress.errorList);
          }
        }
      );

      const sortedResults = [...result.results].sort((a, b) => (
        (typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER) -
        (typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER)
      ));

      setResults(sortedResults);
      setErrors(result.errors);

      // 添加地图标记
      if (sortedResults.length > 0 && map) {
        const newMarkers = mapServiceRef.current.addMarkersToMap(
          map,
          sortedResults,
          handleMarkerClick
        );
        setMarkers(newMarkers);
      } else {
        setMarkers([]);
      }

    } catch (err) {
      console.error('搜索失败:', err);
      alert('搜索失败: ' + (err instanceof Error ? err.message : String(err)));
    } finally {
      setIsSearching(false);
      setSearchProgress(null);
    }
  }, [map, clearMarkers, handleMarkerClick]);

  // 处理结果悬停
  const handleResultHover = useCallback((index: number, hover: boolean) => {
    if (mapServiceRef.current && markers) {
      mapServiceRef.current.highlightMarker(markers, index, hover);
    }
  }, [markers]);

  // 处理地址修正
  const handleCorrectAddress = useCallback((result: GeocodingResult, index: number) => {
    setCurrentResult(result);
    setCurrentResultIndex(index);
    setShowCandidateModal(true);
  }, []);

  // 处理候选地址选择
  const handleCandidateSelect = useCallback((candidate: GeocodingCandidate) => {
    if (currentResultIndex >= 0) {
      const normalizedLocation = mapServiceRef.current
        ? mapServiceRef.current.normalizeLocation(candidate.location)
        : candidate.location;
      const newResults = [...results];
      newResults[currentResultIndex] = {
        ...newResults[currentResultIndex],
        location: normalizedLocation,
        formattedAddress: candidate.formatted_address,
        addressComponent: candidate.addressComponent
      };

      setResults(newResults);

      // 更新地图标记
      if (map && markers[currentResultIndex] &&
          typeof markers[currentResultIndex].setPosition === 'function' &&
          isValidCoordinate(normalizedLocation)) {
        try {
          markers[currentResultIndex].setPosition([normalizedLocation.lng, normalizedLocation.lat]);
        } catch (error) {
          console.warn('更新标记位置失败:', error);
        }
      }
    }
  }, [currentResultIndex, results, map, markers]);

  const handleRetryError = useCallback(async (errorItem: GeocodingError) => {
    if (!mapServiceRef.current) {
      alert('地图服务未初始化');
      return;
    }

    setErrors(prevErrors => prevErrors.map(err => (
      err.address === errorItem.address
        ? { ...err, retrying: true }
        : err
    )));

    try {
      const result = await mapServiceRef.current.geocodeAddress(errorItem.address);

      if (result.success && result.data && result.data.length > 0) {
        const geocode = result.data[0];
        const normalizedLocation = mapServiceRef.current
          ? mapServiceRef.current.normalizeLocation(geocode.location)
          : null;
        const candidates = result.data.length > 1
          ? result.data.slice(1).map(candidate => ({
            ...candidate,
            location: mapServiceRef.current
              ? mapServiceRef.current.normalizeLocation(candidate.location)
              : null
          }))
          : [];
        const newResult = {
          originalAddress: errorItem.originalAddress || errorItem.address,
          fullAddress: errorItem.address,
          location: normalizedLocation,
          formattedAddress: geocode.formatted_address,
          addressComponent: geocode.addressComponent,
          candidates,
          order: typeof errorItem.order === 'number'
            ? errorItem.order
            : Number.MAX_SAFE_INTEGER
        };

        let updatedResults: GeocodingResult[] = [];
        setResults(prevResults => {
          const filtered = prevResults.filter(item => item.order !== newResult.order);
          const merged = [...filtered, newResult].sort((a, b) => (
            (typeof a.order === 'number' ? a.order : Number.MAX_SAFE_INTEGER) -
            (typeof b.order === 'number' ? b.order : Number.MAX_SAFE_INTEGER)
          ));
          updatedResults = merged;
          return merged;
        });

        setErrors(prevErrors => prevErrors.filter(err => err.address !== errorItem.address));

        if (map && updatedResults.length > 0) {
          clearMarkers();
          const newMarkers = mapServiceRef.current.addMarkersToMap(
            map,
            updatedResults,
            handleMarkerClick
          );
          setMarkers(newMarkers);
        } else {
          clearMarkers();
        }
      } else {
        setErrors(prevErrors => prevErrors.map(err => {
          if (err.address !== errorItem.address) return err;
          return {
            ...err,
            error: result.error || '未知错误',
            code: result.code,
            canRetry: result.canRetry !== false,
            retrying: false
          };
        }));
      }
    } catch (error) {
      setErrors(prevErrors => prevErrors.map(err => {
        if (err.address !== errorItem.address) return err;
        return {
          ...err,
          error: (error instanceof Error ? error.message : String(error)) || '重试失败',
          code: 'EXCEPTION',
          canRetry: true,
          retrying: false
        };
      }));
    }
  }, [map, clearMarkers, handleMarkerClick]);

  // 获取配额统计
  const getQuotaStats = useCallback(() => {
    if (mapServiceRef.current) {
      return mapServiceRef.current.getQuotaStats();
    }
    return {
      requestCount: 0,
      currentDelayMs: 0,
      quotaExceeded: false,
      consecutiveErrors: 0
    };
  }, []);

  // 重置配额
  const handleResetQuota = useCallback(() => {
    if (mapServiceRef.current) {
      mapServiceRef.current.resetQuota();
    }
  }, []);

  // 检查地图是否已初始化
  const isMapInitialized = Boolean(AMap && map && geocoder);

  // 调试日志
  useEffect(() => {
    console.log('地图状态检查:', {
      AMap: AMap,
      AMapExists: !!AMap,
      map: map,
      mapExists: !!map,
      geocoder: geocoder,
      geocoderExists: !!geocoder,
      isMapInitialized: isMapInitialized
    });
  }, [AMap, map, geocoder, isMapInitialized]);

  // 强制更新机制
  useEffect(() => {
    if (map && geocoder && !isMapInitialized) {
      // 如果地图已创建但状态显示未初始化，强制更新
      setTimeout(() => {
        console.log('检测到状态不一致，触发重新渲染');
        setIsSearching(false); // 触发一个小的状态更新来强制重新渲染
      }, 200);
    }
  }, [map, geocoder, isMapInitialized]);

  return (
    <div className="app">
      <Header
        onConfigClick={() => setShowConfig(true)}
        isMapInitialized={isMapInitialized}
        quotaStats={getQuotaStats()}
        onResetQuota={handleResetQuota}
      />

      <AddressInput
        onSearch={handleSearch}
        isSearching={isSearching}
        disabled={!isMapInitialized}
      />

      <div className="main-content">
        <ResultsList
          results={results}
          errors={errors}
          onResultHover={handleResultHover}
          onResultClick={handleResultClick}
          onCorrectAddress={handleCorrectAddress}
          onRetryError={handleRetryError}
          isSearching={isSearching}
          searchProgress={searchProgress}
        />

        <MapContainer
          map={map}
          markers={markers}
          isMapInitialized={isMapInitialized}
          loading={loading}
          error={error}
        />
      </div>

      <ApiKeyConfig
        isOpen={showConfig}
        apiKey={apiKey}
        securityKey={securityKey}
        onClose={() => setShowConfig(false)}
        onSave={handleConfigSave}
        loading={loading}
      />

      <CandidateModal
        isOpen={showCandidateModal}
        result={currentResult}
        onClose={() => setShowCandidateModal(false)}
        onSelect={handleCandidateSelect}
      />
    </div>
  );
}

export default App;
